import { useContext, useState, useEffect } from 'react';
import { LanguageCodes } from '../index';
import { getTranslation } from '../utils/getTranslation';
import { TranslationContext, DEFAULT_LANGUAGE } from './Translation.context';
import { parseSearchParams } from '../../utils/parseSearchParams';
import { EVENTS } from '../../lib/constants';

const availableLanguages = Object.keys(LanguageCodes);
const isWindowDefined = typeof window !== 'undefined';
const getLanguageFromURL = () => {
	if (isWindowDefined) {
		const langParam = parseSearchParams('lang');
		return langParam && availableLanguages.includes(langParam)
			? langParam
			: DEFAULT_LANGUAGE;
	}
	return DEFAULT_LANGUAGE;
};

// Custom event for change language
const languageChangeEvent = (langCode, languagePack) =>
	new CustomEvent(EVENTS.LANGUAGE_CHANGED, {
		detail: { langCode, languagePack }
	});

export const TranslationProvider = ({ children }) => {
	const [currentLanguage, setCurrentLanguage] = useState(getLanguageFromURL());
	const [currentTranslations, setCurrentTranslations] = useState(
		LanguageCodes[currentLanguage] || LanguageCodes[DEFAULT_LANGUAGE] || {}
	);

	useEffect(() => {
		if (!isWindowDefined) return;

		const urlLang = parseSearchParams('lang');
		const validLang =
			urlLang && availableLanguages.includes(urlLang)
				? urlLang
				: DEFAULT_LANGUAGE;

		const langToUse =
			validLang !== currentLanguage ? validLang : currentLanguage;
		const newTranslations =
			LanguageCodes[langToUse] || LanguageCodes[DEFAULT_LANGUAGE] || {};

		setCurrentLanguage(langToUse);
		setCurrentTranslations(newTranslations);

		window.dispatchEvent(languageChangeEvent(langToUse, newTranslations));
	}, [currentLanguage]);

	const contextValue = {
		currentLanguage,
		t: getTranslation(currentTranslations, currentLanguage),
		availableLanguages
	};

	return (
		<TranslationContext.Provider value={contextValue}>
			{children}
		</TranslationContext.Provider>
	);
};

export const useTranslation = () => {
	const context = useContext(TranslationContext);
	if (!context) {
		throw new Error('useTranslation must be used within a TranslationProvider');
	}
	return context;
};
